import { useState, useCallback } from 'react'
import type * as jsforce from 'jsforce'
import type {
  PRSheetData,
  PRAIPrompt,
  PRSkill,
  PREducation,
  PRLink,
  PRExperience,
  PRPortfolioItem,
  PRHighlightEpisode,
  PRAward,
} from '../types/salesforce'
import { generateAIContent, applyAIGeneration, discardAIGeneration } from '../services/salesforce'

/**
 * AI生成機能管理フック
 *
 * 責務:
 * - AI生成ダイアログの開閉状態管理
 * - AI生成処理（生成リクエスト → バックアップ作成 → データ反映）
 * - 確認モード状態管理（確定/破棄ボタン表示制御）
 * - バックアップデータ管理
 */
export function useAIGeneration(
  data: PRSheetData | null,
  aiPrompt: PRAIPrompt | null,
  onDataUpdate: (data: PRSheetData) => void,
  onSuccess: (message: string) => void,
  onError: (message: string, isSessionExpired: boolean) => void,
) {
  const [aiDialogOpen, setAiDialogOpen] = useState(false)
  const [aiLoading, setAiLoading] = useState(false)
  const [confirmationMode, setConfirmationMode] = useState(false)
  const [backupData, setBackupData] = useState<PRSheetData | null>(null)
  const [currentGenerationId, setCurrentGenerationId] = useState<string | null>(null)

  const handleAIGenerate = useCallback(
    async (conn: jsforce.Connection, additionalPrompt: string, targetFields: string[]) => {
      if (!data || !data.career.Id || !aiPrompt) return

      setAiLoading(true)
      try {
        const result = await generateAIContent(conn, {
          careerId: data.career.Id,
          promptId: aiPrompt.Id!,
          additionalPrompt,
          targetFields,
        })

        if (result.success && result.generatedContent) {
          // バックアップを作成（深いコピー）
          setBackupData(JSON.parse(JSON.stringify(data)))

          // 生成されたコンテンツをフォームに反映
          const updatedData = { ...data }

          Object.entries(result.generatedContent).forEach(([fieldName, value]) => {
            // 関連オブジェクトデータの処理
            if (fieldName === 'Skill' && Array.isArray(value)) {
              // スキルデータを追加
              updatedData.skills = value.map((skillData: Partial<PRSkill>, index) => ({
                Id: `temp-skill-${Date.now()}-${index}`,
                PRSheet__c: data.prSheet.Id,
                Name: skillData.Name || '',
                Rating__c: skillData.Rating__c,
                Description__c: skillData.Description__c || '',
                SkillGroup__c: undefined, // SkillGroupNameは後で処理
                SortOrder__c: (index + 1) * 10,
              }))
            } else if (fieldName === 'Education' && Array.isArray(value)) {
              updatedData.educations = value.map((eduData: Partial<PREducation>, index) => ({
                Id: `temp-edu-${Date.now()}-${index}`,
                PRSheet__c: data.prSheet.Id,
                Year__c: typeof eduData.Year__c === 'number' ? eduData.Year__c : undefined,
                SchoolName__c: eduData.SchoolName__c || '',
                Department__c: eduData.Department__c || '',
                Status__c: eduData.Status__c || undefined,
                SortOrder__c: (index + 1) * 10,
              }))
            } else if (fieldName === 'Link' && Array.isArray(value)) {
              updatedData.links = value.map((linkData: Partial<PRLink>, index) => ({
                Id: `temp-link-${Date.now()}-${index}`,
                PRSheet__c: data.prSheet.Id,
                LinkName__c: linkData.LinkName__c || '',
                URL__c: linkData.URL__c || '',
                SortOrder__c: (index + 1) * 10,
              }))
            } else if (fieldName === 'Experience' && Array.isArray(value)) {
              updatedData.experiences = value.map((expData: Partial<PRExperience>, index) => ({
                Id: `temp-exp-${Date.now()}-${index}`,
                PRSheet__c: data.prSheet.Id,
                Year__c: typeof expData.Year__c === 'number' ? expData.Year__c : undefined,
                Title__c: expData.Title__c || '',
                Overview__c: expData.Overview__c || '',
                Results__c: expData.Results__c || '',
                SortOrder__c: (index + 1) * 10,
              }))
            } else if (fieldName === 'PortfolioItem' && Array.isArray(value)) {
              updatedData.portfolioItems = value.map((portData: Partial<PRPortfolioItem>, index) => ({
                Id: `temp-port-${Date.now()}-${index}`,
                PRSheet__c: data.prSheet.Id,
                Title__c: portData.Title__c || '',
                ImageContentDocumentId__c: portData.ImageContentDocumentId__c || undefined,
                Overview__c: portData.Overview__c || '',
                Role__c: portData.Role__c || '',
                URL__c: portData.URL__c || '',
                SortOrder__c: (index + 1) * 10,
              }))
            } else if (fieldName === 'HighlightEpisode' && Array.isArray(value)) {
              updatedData.highlightEpisodes = value.map((epData: Partial<PRHighlightEpisode>, index) => ({
                Id: `temp-ep-${Date.now()}-${index}`,
                PRSheet__c: data.prSheet.Id,
                Title__c: epData.Title__c || '',
                ImageContentDocumentId__c: epData.ImageContentDocumentId__c || undefined,
                Summary__c: epData.Summary__c || '',
                Goal__c: epData.Goal__c || '',
                Challenges__c: epData.Challenges__c || '',
                Team__c: epData.Team__c || '',
                Role__c: epData.Role__c || '',
                Actions__c: epData.Actions__c || '',
                Results__c: epData.Results__c || '',
                SortOrder__c: (index + 1) * 10,
              }))
            } else if (fieldName === 'Award' && Array.isArray(value)) {
              updatedData.awards = value.map((awardData: Partial<PRAward>, index) => ({
                Id: `temp-award-${Date.now()}-${index}`,
                PRSheet__c: data.prSheet.Id,
                Date__c: awardData.Date__c || undefined,
                AwardName__c: awardData.AwardName__c || '',
                Details__c: awardData.Details__c || '',
                SortOrder__c: (index + 1) * 10,
              }))
            } else if (fieldName in updatedData.prSheet) {
              // PRSheetオブジェクトのフィールドを更新
              ;(updatedData.prSheet as Record<string, unknown>)[fieldName] = value
            }
          })

          onDataUpdate(updatedData)
          setCurrentGenerationId(result.generationId)
          setConfirmationMode(true)
          setAiDialogOpen(false)
          onSuccess('AI生成が完了しました。内容を確認してください')
        } else {
          onError(result.error || 'AI生成に失敗しました', false)
        }
      } catch (error) {
        console.error('AI生成エラー:', error)

        // セッションエラーの場合はログイン画面にリダイレクト
        const errorMessage = error instanceof Error ? error.message : String(error)
        const isSessionExpired = errorMessage.includes('INVALID_SESSION_ID') || errorMessage.includes('Session expired')

        onError('AI生成に失敗しました', isSessionExpired)
      } finally {
        setAiLoading(false)
      }
    },
    [data, aiPrompt, onDataUpdate, onSuccess, onError],
  )

  const handleConfirmAIGeneration = useCallback(
    async (conn: jsforce.Connection) => {
      if (!currentGenerationId) return

      try {
        await applyAIGeneration(conn, currentGenerationId)
        setConfirmationMode(false)
        setBackupData(null)
        setCurrentGenerationId(null)
        onSuccess('AI生成を確定しました')
      } catch (error) {
        console.error('AI生成確定エラー:', error)
        onError('AI生成の確定に失敗しました', false)
      }
    },
    [currentGenerationId, onSuccess, onError],
  )

  const handleDiscardAIGeneration = useCallback(
    async (conn: jsforce.Connection) => {
      if (!currentGenerationId) return

      try {
        await discardAIGeneration(conn, currentGenerationId)
        // バックアップデータに戻す
        if (backupData) {
          onDataUpdate(backupData)
        }
        setConfirmationMode(false)
        setBackupData(null)
        setCurrentGenerationId(null)
        onSuccess('AI生成を破棄しました')
      } catch (error) {
        console.error('AI生成破棄エラー:', error)
        onError('AI生成の破棄に失敗しました', false)
      }
    },
    [currentGenerationId, backupData, onDataUpdate, onSuccess, onError],
  )

  return {
    aiDialogOpen,
    setAiDialogOpen,
    aiLoading,
    confirmationMode,
    handleAIGenerate,
    handleConfirmAIGeneration,
    handleDiscardAIGeneration,
  }
}
